import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { adminApi, getErrorMessage } from "@/lib/api";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  // No token is ever stored in localStorage/JS state - the session lives in an httpOnly
  // cookie set by the backend. We only keep the (non-sensitive) username in memory, and
  // verify session validity via GET /admin/me on load.
  const [username, setUsername] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const res = await adminApi.get("/admin/me");
      setUsername(res.data.username);
    } catch (e) {
      setUsername(null);
    } finally {
      setCheckingSession(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (u, p, rememberMe = true) => {
    setLoading(true);
    try {
      const res = await adminApi.post("/admin/login", { username: u, password: p, remember_me: rememberMe });
      setUsername(res.data.username);
      return { success: true };
    } catch (e) {
      return { success: false, message: getErrorMessage(e, "Kullanıcı adı veya şifre hatalı.") };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.post("/admin/logout");
    } catch (e) {
      // Session cookie may already be invalid/expired - proceed with local logout regardless.
    } finally {
      setUsername(null);
    }
  }, []);

  /** "Ayarlar" sekmesinden kullanıcı adı/şifre güncelleme. */
  const updateCredentials = useCallback(async ({ currentPassword, newUsername, newPassword }) => {
    try {
      const res = await adminApi.patch("/admin/credentials", {
        current_password: currentPassword,
        new_username: newUsername || null,
        new_password: newPassword || null,
      });
      setUsername(res.data.username);
      return { success: true, username: res.data.username };
    } catch (e) {
      return { success: false, message: getErrorMessage(e, "Bilgiler güncellenemedi.") };
    }
  }, []);

  return (
    <AdminAuthContext.Provider
      value={{ username, isAuthenticated: !!username, login, logout, updateCredentials, loading, checkingSession }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return ctx;
}

